import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { buildBreadcrumbLd } from '../lib/structuredData';

const H2 = ({ children, id }) => (
  <h2 id={id} className="h-display text-2xl md:text-[1.7rem] text-[var(--rs-ink)] mt-10 mb-3 leading-snug scroll-mt-24">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-[var(--rs-ink)]/80 leading-relaxed mt-3">{children}</p>
);

const UL = ({ children }) => (
  <ul className="my-4 space-y-2 list-disc pl-6 text-[var(--rs-ink)]/80">{children}</ul>
);

const Terms = () => {
  return (
    <div>
      <PageMeta
        title="Terms of Use · Residential Support"
        description="The website terms of use for Residential Support — operated by Agents Support Ltd, company no. 1624825. Governed by the laws of England and Wales."
        path="/terms"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Terms', path: '/terms' },
            ])
          )}
        </script>
      </Helmet>

      {/* Header */}
      <section className="bg-[var(--rs-cream)] border-b border-[#E7E0D2]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-10">
          <nav aria-label="Breadcrumb" className="text-xs text-[var(--rs-ink)]/55 mb-5">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-[var(--rs-coral)]">Home</Link></li>
              <li><ChevronRight className="w-3 h-3 text-[var(--rs-ink)]/40" /></li>
              <li className="text-[var(--rs-ink)]/80 font-medium">Terms</li>
            </ol>
          </nav>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Legal</div>
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl text-[var(--rs-ink)] leading-[1.05]" data-testid="terms-title">
            Terms of Use
          </h1>
          <p className="mt-4 text-[var(--rs-ink)]/65 text-sm">Last updated: January 2026</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12 lg:py-16" data-testid="terms-body">
          <P>This website is operated by:</P>
          <UL>
            <li><strong>Residential Support</strong> (a trading name of Agents Support Ltd)</li>
            <li>Company number: 1624825</li>
            <li>20 Allandale Road, Leicester, England, LE2 2DA</li>
          </UL>

          <H2 id="website-use">Website use</H2>
          <P>By using this website you agree to these terms.</P>
          <UL>
            <li>Content is for general information only</li>
            <li>We do not guarantee accuracy or completeness</li>
            <li>Use at your own risk</li>
            <li>We accept no liability for losses arising from website use</li>
            <li>Unauthorised use may lead to legal action</li>
            <li>External links are for convenience only</li>
            <li>You must not create misleading links</li>
            <li>We may withdraw linking permission at any time</li>
            <li>Our services are primarily intended for business users, however individual consumers may also use certain features</li>
          </UL>

          <H2 id="governing-law">Governing law</H2>
          <P>These terms are governed by the laws of England and Wales.</P>

          <H2 id="contact">Contact</H2>
          <P>
            Questions about these terms? Email{' '}
            <a className="text-[var(--rs-coral)] hover:underline" href="mailto:info@residentialsupport.co.uk">
              info@residentialsupport.co.uk
            </a>{' '}
            or call 03333 445 336.
          </P>

          <div className="mt-14 pt-8 border-t border-[#E7E0D2] text-sm text-[var(--rs-ink)]/65">
            <p>
              Looking for our privacy practices?{' '}
              <Link to="/privacy" className="text-[var(--rs-coral)] hover:underline">Read the Privacy Policy &amp; Cookie Policy</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
